import { create } from "zustand";
import type { Days } from "../lib/schedule-presets/art-metadata";
import { weekdayKeys } from "../lib/schedule-time/schedule-dates";

export function dayLayoutSignature(day: Days[keyof Days]): string {
  const { origin, ...layout } = day;
  return JSON.stringify(layout, (key, value) => {
    if (key === "rotation" && value === 0) return undefined;
    if (key === "scale" && value === 100) return undefined;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)));
    }
    return value;
  });
}

// Track the last matching layout during this editing session; never write sync state into metadata.
export const useDayLayoutSyncStore = create<{
  presetId: string | null;
  matches: Record<string, string>;
  observe: (presetId: string, days: Days) => void;
}>(set => ({
  presetId: null,
  matches: {},
  observe: (presetId, days) => set(state => {
    const matches = state.presetId === presetId ? { ...state.matches } : {};
    const signatures = Object.fromEntries(weekdayKeys.map(day => [day, dayLayoutSignature(days[day])]));
    weekdayKeys.forEach(source => weekdayKeys.forEach(target => {
      if (source === target) return;
      const pair = `${source}:${target}`;
      if (signatures[source] === signatures[target]) matches[pair] = signatures[source]!;
      else if (matches[pair] !== signatures[target]) delete matches[pair];
    }));
    return { presetId, matches };
  }),
}));
